import React, { useState, useEffect, useCallback } from 'react';
import { useDataProvider, Title } from 'react-admin';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { PROVIDER_AVAILABILITY_RESOURCE_NAME, AvailabilitySlot } from '../availability-schedules.model';
import CalendarFilterBar from './CalendarFilterBar';
import CalendarGrid from './CalendarGrid';

const AvailabilityCalendarPage: React.FC = () => {
  const dataProvider = useDataProvider();
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const fetchData = useCallback(
    async (currentFilters: Record<string, string>) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await dataProvider.getList(PROVIDER_AVAILABILITY_RESOURCE_NAME, {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'day_of_week', order: 'ASC' },
          filter: currentFilters,
        });
        setSlots(data as AvailabilitySlot[]);
      } catch (err: any) {
        setError(err?.message || 'Failed to load availability data');
        setSlots([]);
      } finally {
        setLoading(false);
      }
    },
    [dataProvider],
  );

  useEffect(() => {
    fetchData(filters);
  }, [fetchData, filters]);

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Provider Availability" />
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Provider Availability
      </Typography>

      <CalendarFilterBar onFilterChange={handleFilterChange} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && slots.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No availability data found. Check that providers have recurring availability configured in
          Healthie.
        </Alert>
      )}

      {!loading && !error && slots.length > 0 && <CalendarGrid slots={slots} />}
    </Box>
  );
};

export default AvailabilityCalendarPage;
